import { _ } from "svelte-i18n"

// fetch polyfill
// [MIT License](LICENSE.md) © [Jason Miller](https://jasonformat.com/)
const _f = function (url: string, options?: { [key: string]: any }) {
    if (typeof XMLHttpRequest === "undefined") {
        return Promise.resolve(null)
    }

    options = options || {}
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()
        const keys: string[] = []
        // @ts-ignore
        const all = []
        const headers = {}

        const response = () => ({
            ok: ((request.status / 100) | 0) == 2, // 200-299
            statusText: request.statusText,
            status: request.status,
            url: request.responseURL,
            text: () => Promise.resolve(request.responseText),
            json: () => Promise.resolve(request.responseText).then(JSON.parse),
            blob: () => Promise.resolve(new Blob([request.response])),
            clone: response,
            headers: {
                // @ts-ignore
                keys: () => keys,
                // @ts-ignore
                entries: () => all,
                // @ts-ignore
                get: (n) => headers[n.toLowerCase()],
                // @ts-ignore
                has: (n) => n.toLowerCase() in headers,
            },
        })

        request.open(options.method || "get", url, true)

        request.onload = () => {
            request
                .getAllResponseHeaders()
                // @ts-ignore
                .replace(/^(.*?):[^\S\n]*([\s\S]*?)$/gm, (m, key, value) => {
                    keys.push((key = key.toLowerCase()))
                    all.push([key, value])
                    // @ts-ignore
                    headers[key] = headers[key] ? `${headers[key]},${value}` : value
                })
            resolve(response())
        }

        request.onerror = reject

        request.withCredentials = options.credentials == "include"

        for (const i in options.headers) {
            request.setRequestHeader(i, options.headers[i])
        }

        request.send(options.body || null)
    })
}
// fetch must be declared after sentry import to get the hijacked fetch
export const _fetch = typeof window === "undefined" ? _f : window.fetch

export const api = async <T>(
    endpoint: string,
    options?: ApiOptions,
    body?: any
): Promise<{ data: T; count: number } | any> => {
    if (!options.headers) options.headers = {}
    if (options.method) options.method = options.method.toUpperCase()

    //let data = await apiRequest(endpoint, options, body, sentry, _fetch)

    // return data
}

type CollectionName = ""
type EntryTypeSwitch<T> = void

const cache: {
    [key: string]: {
        expire: number
        data: any
    }
} = {}

export async function getDBEntries<T extends CollectionName>(
    collectionName: T,
    filter?: { [key: string]: any },
    sort: string = "sort",
    limit?: number,
    offset?: number,
    projection?: string,
    apiPropsCallback?: (props: { count: number }) => any
): Promise<EntryTypeSwitch<T>[]> {
    const c = await api<EntryTypeSwitch<T>[]>(collectionName, { filter, sort, limit, offset, projection })
    apiPropsCallback?.({ count: c.count })
    return c.data
}

export async function getCachedEntries<T extends CollectionName>(
    collectionName: T,
    filter?: { [key: string]: any },
    sort: string = "sort",
    limit?: number,
    offset?: number,
    projection?: string,
    apiPropsCallback?: (props: { count: number }) => any
): Promise<EntryTypeSwitch<T>[]> {
    const filterStr = void 0 // objd2str({ collectionName, filter, sort, limit, offset, projection })
    if (cache[filterStr] && cache[filterStr].expire >= Date.now()) {
        return cache[filterStr].data
    }
    const entries = await getDBEntries<T>(collectionName, filter, sort, limit, offset, projection, apiPropsCallback)
    const inOneHour = Date.now() + 1000 * 60 * 60
    cache[filterStr] = { expire: inOneHour, data: entries }
    return entries
}

export async function getDBEntry<T extends CollectionName>(
    collectionName: T,
    filter: { [key: string]: any },
    projection?: string
) {
    return (await getDBEntries<T>(collectionName, filter, "_id", null, null, projection))?.[0]
}

export async function getCachedEntry<T extends CollectionName>(collectionName: T, filter: { [key: string]: any }) {
    return (await getCachedEntries<T>(collectionName, filter, "_id"))?.[0]
}

export async function postDBEntry<T extends CollectionName>(collectionName: T, entry: EntryTypeSwitch<T>) {
    return api<EntryTypeSwitch<T>>(collectionName, { method: "POST" }, entry)
}

export async function putDBEntry<T extends CollectionName>(collectionName: T, entry: EntryTypeSwitch<Partial<T>>) {
    return api<EntryTypeSwitch<T>>(collectionName + "/" + entry.id, { method: "PUT" }, entry)
}

export async function deleteDBEntry<T extends CollectionName>(collectionName: T, id: string) {
    return api<EntryTypeSwitch<T>>(collectionName + "/" + id, {
        method: "DELETE",
    })
}
